import { type Modification, type ModificationsMap, type ProductIngredients } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a list of modifications based on the provided ingredients and modifiers map.
 * It accumulates and updates modifications with respect to the available ingredient portions.
 *
 * When a lineage map is provided, ingredients whose id is not on the current
 * product fall back to the modification sharing their lineage key. This lets a
 * line item from another restaurant resolve against the local menu.
 */
export function generateModificationsList(
  ingredients: ProductIngredients,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: ModificationsMap,
) {
  const modificationsMap = ingredients.reduce<ModificationsMap>((map, ingredient) => {
    const modification = resolveModification(ingredient, allModificationsMap, lineageModificationsMap); // prettier-ignore

    // ─── Invalid modification ────────────────────────────────────────────

    if (!modification) return map;

    // The fallback is just only satisfies the nullable generated type.
    const ingredientId = modification.ingredient?.id ?? ingredient.id;

    const maybeExistingModification = map.get(ingredientId);

    // ─── Bump quantity if multiple portions are available ────────────────

    if (maybeExistingModification) {
      map.set(ingredientId, withOutOfStockFlag(withUpdatedQuantity(maybeExistingModification))); // prettier-ignore

      return map;
    }

    // ─── Initial portion ─────────────────────────────────────────────────

    map.set(ingredientId, withOutOfStockFlag(modification));

    return map;
  }, new Map());

  return {
    modificationsMap,
    modificationsList: [...modificationsMap.values()],
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the product modification for an ingredient by id, then by lineage key.
 */
export function resolveModification(
  ingredient: Pick<ProductIngredients[number], 'id' | 'lineageKey'>,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: ModificationsMap,
) {
  const modificationById = allModificationsMap.get(ingredient.id);

  if (modificationById) {
    return modificationById;
  }

  const lineageKey = ingredient.lineageKey;

  if (!lineageKey || !lineageModificationsMap) {
    return;
  }

  return lineageModificationsMap.get(lineageKey);
}

function withUpdatedQuantity(modification: Modification): Modification {
  return { ...modification, quantity: modification.quantity + 1 };
}

function withOutOfStockFlag(modification: Modification): Modification {
  return { ...modification, outOfStock: modification.outOfStock };
}
